import { api } from '@/lib/api/client';
import type { AuthMeData } from '@/lib/shared-types/api/endpoints/auth';
import { canAccessAdminArea, getRoleName } from '@/lib/constants/roles';
import { toast } from 'sonner';

export type ProtectedRouteType = 'stream' | 'admin' | 'invite';

interface AccessCheckResult {
  allowed: boolean;
  reason?: string;
  redirectTo?: string;
}

const protectedRoutePatterns: Record<string, ProtectedRouteType> = {
  '/streams/': 'stream',
  '/admin/': 'admin',
  '/join/': 'invite',
};

export function identifyProtectedRoute(url: string): ProtectedRouteType | null {
  for (const [pattern, type] of Object.entries(protectedRoutePatterns)) {
    if (url.startsWith(pattern)) {
      return type;
    }
  }
  return null;
}

export async function checkNotificationAccess(url: string): Promise<AccessCheckResult> {
  const routeType = identifyProtectedRoute(url);

  if (!routeType) {
    return { allowed: true };
  }

  switch (routeType) {
    case 'stream':
      return checkStreamAccess(url);
    case 'admin':
      return checkAdminAccess(url);
    case 'invite':
      return checkInviteAccess(url);
    default:
      return { allowed: true };
  }
}

async function checkStreamAccess(url: string): Promise<AccessCheckResult> {
  const match = url.match(/\/streams\/(\d+)/);
  if (!match) {
    return { allowed: true };
  }

  const streamId = match[1];

  try {
    const response = await api.get<{
      hasAccess: boolean;
      accessType?: string;
      isOwner?: boolean;
      isLive?: boolean;
    }>(`api/streams/${streamId}/access/check`);

    if (!response.success) {
      console.error('Stream access check failed:', response.error);
      return { 
        allowed: false, 
        reason: 'Unable to verify access. Please try again.',
        redirectTo: '/streams'
      };
    }

    const data = response.data;
    if (!data?.hasAccess && !data?.isLive) {
      return {
        allowed: false,
        reason: 'You no longer have access to this stream.',
        redirectTo: `/streams/${streamId}/request-access`
      };
    }

    return { allowed: true };
  } catch (error) {
    console.error('Stream access check error:', error);
    return { allowed: true };
  }
}

async function checkAdminAccess(url: string): Promise<AccessCheckResult> {
  try {
    const response = await api.get<AuthMeData>('api/auth/me');

    if (!response.success || !response.data) {
      return {
        allowed: false,
        reason: 'Unable to verify admin access.',
        redirectTo: '/dashboard'
      };
    }

    const roleName =
      response.data.user?.role ||
      (typeof response.data.user?.roleId === 'number' ? getRoleName(response.data.user.roleId) : undefined) ||
      (typeof response.data.roleId === 'number' ? getRoleName(response.data.roleId) : undefined);

    if (!roleName || !canAccessAdminArea(roleName)) {
      return {
        allowed: false,
        reason: 'You no longer have access to this admin area.',
        redirectTo: '/dashboard'
      };
    }

    return { allowed: true };
  } catch (error) {
    console.error('Admin access check error:', error);
    return { allowed: true };
  }
}

async function checkInviteAccess(url: string): Promise<AccessCheckResult> {
  const match = url.match(/\/join\/([^/?#]+)/);
  if (!match) {
    return { allowed: true };
  }

  const inviteCode = match[1];

  try {
    const response = await api.post<{
      isValid: boolean;
      streamId?: number;
      error?: string;
    }>('api/invites/verify', { code: inviteCode });

    if (!response.success || !response.data?.isValid) {
      return {
        allowed: false,
        reason: 'This invitation is no longer valid or has expired.',
        redirectTo: '/streams'
      };
    }

    return { allowed: true };
  } catch (error) {
    console.error('Invite access check error:', error);
    return { allowed: true };
  }
}

export async function handleNotificationNavigation(
  url: string,
  onNavigate: (url: string) => void
): Promise<void> {
  const accessResult = await checkNotificationAccess(url);

  if (!accessResult.allowed) {
    toast.error(accessResult.reason || 'Access denied');
    
    if (accessResult.redirectTo) {
      onNavigate(accessResult.redirectTo);
    }
    return;
  }

  onNavigate(url);
}
